import { Component, OnInit, AfterViewChecked, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { FacilityObject } from 'src/app/core/services/facility.model';
import { FacilityService } from 'src/app/core/services/facility.service';
import { ServicePageService } from '../service.service';
import { RootScopeDeclare } from 'src/app/core/rootscope-declare';
import { RootScopeService } from 'src/app/core/shared-service/root-scope.service';
import { HaloCustomLoaderErrorType } from 'src/app/core/halo-custom-loader/halo-custom-loader.service';

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
})
export class HistoryComponent implements OnInit, AfterViewChecked, OnDestroy {
  currentFacility: FacilityObject;
  sub = new Subscription();

  data: any;
  showHistoryModal = false;
  customLoaderIsLoading = false;
  customLoaderErrorMsg: string;
  source: string;
  historyList = [] as any;
  filteredHistoryList: any[] = [];
  cacheFilteredHistoryList: any[] = [];
  selectedEquipmentActivityId = '-1';
  from = 0;
  to = 5;
  rootScopeData: RootScopeDeclare;

  constructor(
    private servicePageService: ServicePageService,
    private facilityService: FacilityService,
    private _rootScopeService: RootScopeService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const getRootScopeSub = this._rootScopeService.getRootScope().subscribe((val) => {
      this.rootScopeData = val;
    });
    this.sub.add(getRootScopeSub);
    this.currentFacility = this.facilityService.selectedFacility();
    this.loadHistoryData();
    this.trackingFacilityChanges();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onSelectionChange(selectionInfo: any) {
    this.selectedEquipmentActivityId = selectionInfo;
    this.updateFilteredHistoryData();
  }

  updateFilteredHistoryData(): void {
    if (this.selectedEquipmentActivityId === '-1') {
      this.filteredHistoryList = this.historyList.map((val:any) => ({
        ...val,
        historyDetails: val.historyDetails.filter((item:any) => item.asset)
      }));
    } else {
      if (this.selectedEquipmentActivityId === '2' || this.selectedEquipmentActivityId === '3') {
        let selectedEquipmentActivityIdArray = ['2', '3'];
        this.filteredHistoryList = this.historyList
          .map((val: any) => ({
            ...val,
            historyDetails: val.historyDetails.filter((item: any) =>
              item.asset &&
              selectedEquipmentActivityIdArray.includes(item.asset.activityId?.toString())
            ),
          }))
          .filter((item: any) => !!item.historyDetails.length);
      } else {
        this.filteredHistoryList = this.historyList
          .map((val: any) => ({
            ...val,
            historyDetails: val.historyDetails.filter(
              (item: any) =>
                item .asset &&
                item.asset.activityId &&
                item.asset.activityId?.toString() === this.selectedEquipmentActivityId.toString()
            ),
          }))
          .filter((item: any) => !!item.historyDetails.length);
      }
    }
    this.cacheFilteredHistoryList = JSON.parse(JSON.stringify(this.filteredHistoryList));
    if (this.cacheFilteredHistoryList.slice(this.from, this.to).length) {
      this.filteredHistoryList = this.cacheFilteredHistoryList.slice(this.from, this.to);
      const filterTmpHistoryList = this.filteredHistoryList.filter(val => val.historyDetails.length>0);
      this.filteredHistoryList = filterTmpHistoryList;
    }
  }

  paginationChanged(event: any) {
    this.from = event.currentPage * event.rowsPerPage - event.rowsPerPage;
    this.to = event.currentPage * event.rowsPerPage;
    this.filteredHistoryList = this.cacheFilteredHistoryList.slice(this.from, this.to);
  }

  async loadHistoryData() {
    this.customLoaderIsLoading = true;
    try {
      this.historyList = await this.servicePageService.getHistoryData(this.currentFacility.facilityId);
      this.customLoaderIsLoading = false;
      if (this.historyList) {
        this.historyList = this.historyList
          .filter((item: any) => !!item.historyDetails.length)
          .map((item: any) => ({
            ...item,
            historyDetails: item.historyDetails.map((itemDetail: any) => ({
              ...itemDetail,
              notice:
                itemDetail.notice === 'Equipment Rotated'
                  ? 'Rotation Notice Completed'
                  : itemDetail.notice === 'Belt Inspected'
                  ? 'Belt Notice Completed'
                  : itemDetail.notice,
              actionTakenOn: new Date(itemDetail.actionTakenOn),
            })),
          }));
      } else {
        this.historyList = [];
      }
      this.updateFilteredHistoryData();
    } catch (error) {
      this.customLoaderErrorMsg = HaloCustomLoaderErrorType.APIFAILED;
    }
  }

  private trackingFacilityChanges(): void {
    const facilityChanges = this.facilityService.selectedFacilitySource.subscribe(async (val) => {
      this.currentFacility = val;
      this.loadHistoryData();
    });
    this.sub.add(facilityChanges);
  }

  openHistoryModal(historyDetailItem: any): void {
     if (historyDetailItem.activityType === 1) {
      // Rotation Notes / Rotation Notice Dismissed
      this.data = this.loadEquipmentRotationData(historyDetailItem);
      this.source = 'equipmentRotations';
      this.showHistoryModal = true;
    } else if (historyDetailItem.activityType === 2) {
      // Belt Notice Completed / Belt Notice Completed Dismissed
      this.data = this.loadBeltNotice(historyDetailItem);
      this.source = 'beltNotice';
      this.showHistoryModal = true;
    } else if (historyDetailItem.activityType === 3){
      // Error Codes Completed / Error Codes Dismissed
      this.data = this.loadErrorCodes(historyDetailItem);
      this.source = 'errorCodes';
      this.showHistoryModal = true;
    }
  }

  loadEquipmentRotationData(data: any) {
    return {
      highUsageEquipment: {
        totalMileage: data.highUseTotalMileage,
        totalHours: data.highUseTotalHours,
        displayName: data.highUseAsset?.displayName ? data.highUseAsset?.displayName : data.highUseAsset?.bodySerial,
      },
      lowUsageEquipment: {
        totalMileage: data.lowUseTotalMileage,
        totalHours: data.lowUseTotalHours,
        displayName: data.lowUseAsset?.displayName ? data.lowUseAsset?.displayName : data.lowUseAsset?.bodySerial,
      },
      completedBy: data.actionTakenBy,
      completedDate: data.actionTakenOn,
      notes: data.notes,
      isDismissed: data.status === 'Dismissed',
    };
  }

  loadBeltNotice(data: any) {
    return {
      description: data.asset?.displayName,
      isDismissed: data.status === 'Dismissed' || data.status === 'Closed',
      beltTotal: data.historyTotalMileage,
      beltMileage: data.historyBeltMileage,
      beltHours: data.historyTotalHours,
      inspectedBy: data.actionTakenBy,
      inspectedDate: data.actionTakenOn,
      notes: data.notes,
    };
  }

  loadErrorCodes(data: any) {
    return {
      description: data.asset?.displayName,
      isDismissed: data.status === 'Dismissed' || data.status === 'Closed',
      beltTotal: data.historyTotalMileage,
      beltMileage: data.historyBeltMileage,
      beltHours: data.historyTotalHours,
      inspectedBy: data.actionTakenBy,
      inspectedDate: data.actionTakenOn,
      notes: data.notes,
    };
  }
}
